import * as React from 'react';
import { View } from 'react-native';

interface TargetLayout {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface TourTargetRegistryContextValue {
  registerTarget: (id: string, ref: View | null) => void;
  measureTarget: (id: string) => Promise<TargetLayout | null>;
}

const TourTargetRegistryContext =
  React.createContext<TourTargetRegistryContextValue | null>(null);

interface TourTargetProviderProps {
  children: React.ReactNode;
}

function TourTargetProvider({ children }: TourTargetProviderProps) {
  const targetsRef = React.useRef<Map<string, View>>(new Map());

  const registerTarget = React.useCallback(
    (id: string, ref: View | null) => {
      if (ref === null) {
        const next = new Map(targetsRef.current);
        next.delete(id);
        targetsRef.current = next;
      } else {
        targetsRef.current = new Map(targetsRef.current).set(id, ref);
      }
    },
    []
  );

  const measureTarget = React.useCallback(
    (id: string): Promise<TargetLayout | null> => {
      const view = targetsRef.current.get(id);
      if (!view) {
        return Promise.resolve(null);
      }

      return new Promise((resolve) => {
        view.measureInWindow((x, y, width, height) => {
          resolve({ x, y, width, height });
        });
      });
    },
    []
  );

  const value = React.useMemo(
    () => ({ registerTarget, measureTarget }),
    [registerTarget, measureTarget]
  );

  return (
    <TourTargetRegistryContext.Provider value={value}>
      {children}
    </TourTargetRegistryContext.Provider>
  );
}

function useTourTargets(): TourTargetRegistryContextValue {
  const ctx = React.useContext(TourTargetRegistryContext);
  if (!ctx) {
    throw new Error('useTourTargets must be used within a TourTargetProvider');
  }
  return ctx;
}

export { TourTargetProvider, useTourTargets };
export type { TourTargetRegistryContextValue, TargetLayout };
